import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, ChevronDown, Loader2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useKbQuestionDetail } from "../hooks/useKbQuestionDetail";
import { addKbQuestionAnswer } from "../services/kbQuestionsService";
import { Breadcrumb } from "../components/molecules/Breadcrumb";
import type { KbAnswerVersionItem } from "../types/kbQuestion";
import { formatDisplayDate } from "../utils/displayDate";

type VersionItem = {
  readonly key: string;
  readonly label: string;
  readonly updatedBy: string;
  readonly updatedOn: string;
  readonly content: string;
};

const formatDate = (iso: string | null): string => formatDisplayDate(iso);

const compareVersionsDesc = (a: KbAnswerVersionItem, b: KbAnswerVersionItem) =>
  b.version - a.version;

export const RFPQuestionDetailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [openVersionKeys, setOpenVersionKeys] = useState<readonly string[]>([]);
  const [statusAnnouncement, setStatusAnnouncement] = useState("");
  const [lastKnownActiveState, setLastKnownActiveState] = useState<boolean | null>(null);
  const [isEditingAnswer, setIsEditingAnswer] = useState(false);
  const [editedAnswerText, setEditedAnswerText] = useState("");
  const [isSavingAnswer, setIsSavingAnswer] = useState(false);
  const [answerEditError, setAnswerEditError] = useState<string | null>(null);

  const questionId = searchParams.get("id")?.trim() || null;
  const { detail, loading, isTogglingStatus, error, reload, toggleStatus } = useKbQuestionDetail(questionId);

  const versions = useMemo(
    () => [...(detail?.answer_versions ?? [])].sort(compareVersionsDesc),
    [detail?.answer_versions],
  );

  const latestVersion = useMemo(() => {
    const current = versions.find((version) => version.is_current);
    return current ?? versions[0] ?? null;
  }, [versions]);
  const latestAnswerText = latestVersion?.answer_text ?? "";

  const previousVersions = useMemo<readonly VersionItem[]>(() => {
    if (!latestVersion) return [];
    return versions
      .filter((version) => version.id !== latestVersion.id)
      .map((version) => ({
        key: version.id,
        label: `v${version.version}`,
        updatedBy: version.created_by ?? "Unknown",
        updatedOn: formatDate(version.created_at),
        content: version.answer_text || "No answer text available for this version.",
      }));
  }, [versions, latestVersion]);

  useEffect(() => {
    if (!detail) return;
    if (lastKnownActiveState === null) {
      setLastKnownActiveState(detail.is_active);
      return;
    }
    if (!isTogglingStatus && detail.is_active !== lastKnownActiveState) {
      setStatusAnnouncement(`Question status is now ${detail.is_active ? "active" : "inactive"}.`);
      setLastKnownActiveState(detail.is_active);
    }
  }, [detail, isTogglingStatus, lastKnownActiveState]);

  useEffect(() => {
    if (isTogglingStatus) {
      setStatusAnnouncement("Updating question status...");
    }
  }, [isTogglingStatus]);

  useEffect(() => {
    if (!isEditingAnswer) {
      setEditedAnswerText(latestAnswerText);
    }
  }, [latestAnswerText, isEditingAnswer]);

  const handleStartEditingAnswer = useCallback(() => {
    setEditedAnswerText(latestAnswerText);
    setAnswerEditError(null);
    setIsEditingAnswer(true);
  }, [latestAnswerText]);

  const handleCancelEditingAnswer = useCallback(() => {
    setEditedAnswerText(latestAnswerText);
    setAnswerEditError(null);
    setIsEditingAnswer(false);
  }, [latestAnswerText]);

  const handleSaveAnswer = useCallback(async () => {
    if (!questionId || isSavingAnswer) return;

    const normalizedDraft = editedAnswerText.trim();
    const normalizedCurrent = latestAnswerText.trim();

    if (normalizedDraft === normalizedCurrent) {
      setAnswerEditError("Please update the answer text before saving.");
      return;
    }

    setIsSavingAnswer(true);
    setAnswerEditError(null);
    try {
      await addKbQuestionAnswer(questionId, { answer_text: editedAnswerText, set_as_current: true });
      setIsEditingAnswer(false);
      reload();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to save answer";
      setAnswerEditError(message);
    } finally {
      setIsSavingAnswer(false);
    }
  }, [questionId, isSavingAnswer, editedAnswerText, latestAnswerText, reload]);

  const statusClass = detail?.is_active ? "bg-primary-500" : "bg-gray-300";
  const statusThumbClass = detail?.is_active ? "translate-x-[26px]" : "translate-x-[2px]";
  const breadcrumbItems = [
    { label: "Library", to: "/library" },
    { label: "RFPs", to: "/rfps" },
    { label: "RFP Question Detail" },
  ] as const;

  if (!questionId) {
    return (
      <div className="flex-1 overflow-auto bg-gray-50">
        <Breadcrumb items={breadcrumbItems} />
        {/* <button
          type="button"
          onClick={() => navigate("/rfps")}
          className="mb-4 flex items-center gap-2 text-sm font-medium text-gray-500 transition-colors hover:text-primary-500"
        >
          <ArrowLeft className="h-5 w-5" />
          Back to RFPs
        </button> */}
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
          Missing question id in URL. Open this page from the RFP list action button.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex-1 overflow-auto bg-gray-50">
        <Breadcrumb items={breadcrumbItems} />
        {/* <button
          type="button"
          onClick={() => navigate("/rfps")}
          className="mb-4 flex items-center gap-2 text-sm font-medium text-gray-500 transition-colors hover:text-primary-500"
        >
          <ArrowLeft className="h-5 w-5" />
          Back to RFPs
        </button> */}
        <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
          Loading question details...
        </div>
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div className="flex-1 overflow-auto bg-gray-50">
        <Breadcrumb items={breadcrumbItems} />
        {/* <button
          type="button"
          onClick={() => navigate("/rfps")}
          className="mb-4 flex items-center gap-2 text-sm font-medium text-gray-500 transition-colors hover:text-primary-500"
        >
          <ArrowLeft className="h-5 w-5" />
          Back to RFPs
        </button> */}
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-3 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-red-700 ring-1 ring-red-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex-1 overflow-auto bg-gray-50">
        <Breadcrumb items={breadcrumbItems} />
        {/* <button
          type="button"
          onClick={() => navigate("/rfps")}
          className="mb-4 flex items-center gap-2 text-sm font-medium text-gray-500 transition-colors hover:text-primary-500"
        >
          <ArrowLeft className="h-5 w-5" />
          Back to RFPs
        </button> */}
        <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
          Question details were not found.
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <Breadcrumb items={breadcrumbItems} />
      <div className="mb-6">
        {/* <button
          type="button"
          onClick={() => navigate("/rfps")}
          className="mb-4 flex items-center gap-2 text-sm font-medium text-gray-500 transition-colors hover:text-primary-500"
        >
          <ArrowLeft className="h-5 w-5" />
          Back to RFPs
        </button> */}
        <h1 className="text-3xl font-bold text-heading">RFP Question Detail</h1>
      </div>

      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6">
        <div className="mb-4">
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
            Question
          </label>
          <p className="text-base font-medium leading-relaxed text-gray-900">
            {detail.question_text}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 border-t border-gray-200 pt-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-500">Created By</label>
            <p className="text-sm text-gray-800">
              {latestVersion?.created_by ?? detail.metadata.created_by?.toString() ?? "Unknown"}
            </p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-500">Created On</label>
            <p className="text-sm text-gray-800">{formatDate(detail.created_at)}</p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-500">Section</label>
            <p className="text-sm text-gray-800">{detail.section_name || "—"}</p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-500">Times Used</label>
            <p className="text-sm text-gray-800">{detail.total_times_used}</p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-500">Status</label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  void toggleStatus();
                }}
                disabled={isTogglingStatus}
                role="switch"
                aria-checked={detail.is_active}
                aria-label="Toggle question active status"
                aria-busy={isTogglingStatus}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all ${statusClass} ${isTogglingStatus ? "cursor-wait opacity-70" : ""}`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${statusThumbClass}`}
                />
              </button>
              <span className="text-sm text-gray-800">
                {detail.is_active ? "Active" : "Inactive"}
              </span>
              {isTogglingStatus && (
                <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Updating...
                </span>
              )}
            </div>
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
            <span className="sr-only" aria-live="polite">
              {statusAnnouncement}
            </span>
          </div>
          {/* <div>
            <label className="mb-1 block text-xs font-semibold text-gray-500">Question ID</label>
            <p className="break-all text-sm text-gray-800">{detail.id}</p>
          </div> */}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Answer</h2>

        <div className="mb-6">
          <div className="mb-3 flex items-center justify-between gap-2">
            <span className="rounded bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-600">
              Latest Answer - {latestVersion ? `v${latestVersion.version}` : "—"}
            </span>
            {!isEditingAnswer && (
              <button
                type="button"
                onClick={handleStartEditingAnswer}
                className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                Edit
              </button>
            )}
          </div>

          {isEditingAnswer ? (
            <div className="mb-4">
              <textarea
                value={editedAnswerText}
                onChange={(event) => {
                  setEditedAnswerText(event.target.value);
                  if (answerEditError) {
                    setAnswerEditError(null);
                  }
                }}
                rows={8}
                className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm leading-7 text-gray-700 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                aria-label="Edit latest answer"
              />
              {answerEditError && <p className="mt-2 text-xs text-red-600">{answerEditError}</p>}
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    void handleSaveAnswer();
                  }}
                  disabled={isSavingAnswer}
                  className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isSavingAnswer && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save
                </button>
                <button
                  type="button"
                  onClick={handleCancelEditingAnswer}
                  disabled={isSavingAnswer}
                  className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="mb-4 whitespace-pre-line rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm leading-7 text-gray-700">
              {latestVersion?.answer_text || "No answer version available."}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-4 border-t border-gray-200 pt-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-500">Updated by:</span>
              <span className="font-medium text-gray-800">
                {latestVersion?.created_by ?? "Unknown"}
              </span>
            </div>
            <span className="text-gray-300">•</span>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-500">Updated on:</span>
              <span className="font-medium text-gray-800">
                {formatDate(latestVersion?.created_at ?? detail.updated_at)}
              </span>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <div className="mb-3 text-sm font-semibold text-gray-500">
            Previous Versions ({previousVersions.length})
          </div>

          {previousVersions.length === 0 ? (
            <p className="text-sm text-gray-500">No previous versions available.</p>
          ) : (
            <div className="space-y-2">
              {previousVersions.map((version) => {
                const isOpen = openVersionKeys.includes(version.key);
                return (
                  <div
                    key={version.key}
                    className="overflow-hidden rounded-lg border border-gray-200"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setOpenVersionKeys((currentKeys) =>
                          currentKeys.includes(version.key)
                            ? currentKeys.filter((key) => key !== version.key)
                            : [...currentKeys, version.key],
                        )
                      }
                      className="flex w-full items-center justify-between bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100"
                    >
                      <div className="flex flex-wrap items-center gap-3">
                        <ChevronDown
                          className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
                        />
                        <span className="text-sm font-semibold text-gray-800">{version.label}</span>
                        <span className="text-sm text-gray-500">
                          Updated by {version.updatedBy}
                        </span>
                        <span className="text-sm text-gray-500">• {version.updatedOn}</span>
                      </div>
                      <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
                        Previous Version
                      </span>
                    </button>

                    {isOpen && (
                      <div className="border-t border-gray-200 px-4 py-3">
                        <p className="whitespace-pre-line text-sm leading-7 text-gray-700">
                          {version.content}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
